import * as v from 'valibot'
import {
  type CalendarStatus,
  type CalendarSettingInput,
  type CalendarDailyAvailability,
  type CalendarDailyAvailabilityInput,
  type CalendarAssignedStaff,
  CalendarStaffSelectionStrategy,
  type CalendarAssignedStaffInput,
} from '@/types/schema.generated.ts'
import {
  fromGQLCalendarQuestions,
  toGQLCalendarQuestionInput,
  type Question,
} from './questions/calendarQuestion'
import type { FetchCalendarDetailQuery } from './SettingsPageCalendarDetailView.generated'

export type FormType = {
  privateTitle: string
  publicTitle: string
  description: string
  status: CalendarStatus
  durationMinutes: number
  offsetMinutes: number
  slotGenerationDelayHours: number
  slotGenerationPeriodDays: number
  dailyAvailabilities: CalendarDailyAvailabilityInput[]
  assignedStaffs: {
    guid: string
    priority?: number
    displayName: string
  }[]
  staffSelectionStrategy: CalendarStaffSelectionStrategy
  questions: Question[]
}

// Conditional validation for 'priority' based on 'staffSelectionStrategy' is handled below.
const baseFormSchema = v.object({
  publicTitle: v.pipe(
    v.string('公開用タイトルは文字列である必要があります'),
    v.trim(),
    v.nonEmpty('公開用タイトルは必須です'),
    v.maxLength(256, '公開用タイトルは256文字以下にしてください')
  ),
  privateTitle: v.pipe(
    v.string('管理用タイトルは文字列である必要があります'),
    v.trim(),
    v.maxLength(256, '管理用タイトルは256文字以下にしてください')
  ),
  description: v.string(),
  status: v.picklist(
    ['ACTIVE', 'INACTIVE', 'DELETED'] as CalendarStatus[],
    'カレンダーの状態を選択してください'
  ),
  durationMinutes: v.pipe(
    v.number('所要時間は数値で入力してください'),
    v.minValue(10, '所要時間は10分以上にしてください'),
    v.maxValue(720, '所要時間は720分以下にしてください')
  ),
  offsetMinutes: v.pipe(
    v.number('表示間隔は数値で入力してください'),
    v.minValue(10, '表示間隔は10分以上にしてください'),
    v.maxValue(720, '表示間隔は720分以下にしてください')
  ),
  slotGenerationDelayHours: v.pipe(
    v.number('枠表示開始時間は数値で入力してください'),
    v.minValue(0, '枠表示開始時間は0時間以上にしてください'),
    v.maxValue(720, '枠表示開始時間は720時間以下にしてください')
  ),
  slotGenerationPeriodDays: v.pipe(
    v.number('枠表示期間は数値で入力してください'),
    v.minValue(1, '枠表示期間は1日以上にしてください'),
    v.maxValue(200, '枠表示期間は200日以下にしてください')
  ),
  staffSelectionStrategy: v.picklist(
    ['RANDOM', 'PRIORITY'] as CalendarStaffSelectionStrategy[],
    '担当者の選出方法を選択してください'
  ),
  assignedStaffs: v.pipe(
    v.array(v.any()),
    v.minLength(1, '担当者を1人以上選択してください')
  ),
  questions: v.array(
    v.object({
      type: v.picklist(['TEXT', 'SELECT_BOX']),
      questionText: v.pipe(
        v.string('質問文は文字列である必要があります'),
        v.trim(),
        v.nonEmpty('質問文は必須です')
      ),
      status: v.any(),
      options: v.optional(
        v.pipe(
          v.array(
            v.pipe(
              v.string('選択肢は文字列である必要があります'),
              v.trim(),
              v.nonEmpty('選択肢が空です')
            )
          ),
          v.minLength(2, '選択肢は2つ以上にしてください'),
          v.check(options => {
            const uniqueOptions = new Set(options)
            return uniqueOptions.size === options.length
          }, '選択肢に重複があります')
        )
      ),
    })
  ),
})

// validFormSchema remains baseFormSchema, conditional logic is added below.
const validFormSchema = baseFormSchema

export const validateForm = (form: FormType) => {
  const result = v.safeParse(validFormSchema, form, {
    abortPipeEarly: true,
  })

  if (result.success) {
    return undefined
  }

  const errors = v.flatten(result.issues).nested

  const collectQuestionsErrors = (): (string | undefined)[] => {
    if (!form.questions || form.questions.length === 0) {
      return []
    }

    return (
      Object.entries(errors || {})
        // questions の各エラーは errors の中で `errors?.['questions.0.questionText']` という形式になっている
        .filter(([key]) => key.match(/^questions\.(\d+)\./))
        .reduce((acc, [key, value]) => {
          const match = key.match(/^questions\.(\d+)\./)!
          const index = parseInt(match[1], 10)
          // 各質問について最初のエラーだけ保持する
          if (acc[index] === undefined) {
            acc[index] = value![0]
          }
          return acc
        }, new Array<string | undefined>(form.questions.length).fill(undefined))
    )
  }

  return {
    publicTitle: errors?.publicTitle?.[0],
    privateTitle: errors?.privateTitle?.[0],
    description: errors?.description?.[0],
    status: errors?.status?.[0],
    durationMinutes: errors?.durationMinutes?.[0],
    offsetMinutes: errors?.offsetMinutes?.[0],
    slotGenerationDelayHours: errors?.slotGenerationDelayHours?.[0],
    slotGenerationPeriodDays: errors?.slotGenerationPeriodDays?.[0],
    dailyAvailabilities: errors?.dailyAvailabilities?.[0],
    staffSelectionStrategy: errors?.staffSelectionStrategy?.[0],
    assignedStaffs: errors?.assignedStaffs?.[0],
    questions: collectQuestionsErrors(),
  }
}

type CalendarAssignedStaffTypeInFormStyle = {
  guid: string
  displayName: string
  priority?: number
}

const convertCalendarAssignedStaffsToFormStyle = (
  calendarAssignedStaffs: CalendarAssignedStaff[],
  staffSelectionStrategy: CalendarStaffSelectionStrategy
): CalendarAssignedStaffTypeInFormStyle[] => {
  const result = calendarAssignedStaffs.map(
    (staff: CalendarAssignedStaff): CalendarAssignedStaffTypeInFormStyle => ({
      guid: staff.guid,
      displayName: staff.displayName,
      priority: staff.priority ?? undefined,
    })
  )

  if (staffSelectionStrategy === CalendarStaffSelectionStrategy.Random) {
    return result
  }

  return result.toSorted((a, b) => {
    if (a.priority && b.priority) {
      return a.priority - b.priority
    }
    return 0
  })
}

const convertFormStyleAssignedStaffsToGQLInputStyle = (
  formAssignedStaffs: CalendarAssignedStaffTypeInFormStyle[],
  staffSelectionStrategy: CalendarStaffSelectionStrategy
): CalendarAssignedStaffInput[] => {
  if (staffSelectionStrategy === CalendarStaffSelectionStrategy.Random) {
    const staffsWithNoPriority = formAssignedStaffs.map(
      (
        s: CalendarAssignedStaffTypeInFormStyle
      ): CalendarAssignedStaffInput => ({
        guid: s.guid,
        priority: undefined,
      })
    )
    return staffsWithNoPriority
  }

  const isInvalidAssignedStaffs = formAssignedStaffs.some(
    s => s.priority === undefined
  )
  if (isInvalidAssignedStaffs) {
    throw new Error(
      'priority must be set for all assigned staff when selection strategy is "priority"'
    )
  }

  const alignedStaffs = formAssignedStaffs
    .toSorted((a, b) => {
      if (a.priority && b.priority) {
        return a.priority - b.priority
      }
      return 0
    })
    .map(
      (
        s: CalendarAssignedStaffTypeInFormStyle,
        index: number
      ): CalendarAssignedStaffInput => ({
        guid: s.guid,
        priority: index + 1,
      })
    )
  return alignedStaffs
}

export const mapCalendarToForm = (
  calendar: FetchCalendarDetailQuery['calendarDetail']
): FormType => {
  const assignedStaffs = convertCalendarAssignedStaffsToFormStyle(
    calendar.assignedStaffs,
    calendar.staffSelectionStrategy
  )

  return {
    status: calendar.status,
    privateTitle: calendar.privateTitle ?? '',
    publicTitle: calendar.publicTitle,
    description: calendar.description ? calendar.description : '',
    durationMinutes: calendar.slotDurationMinutes,
    offsetMinutes: calendar.slotOffsetMinutes,
    slotGenerationDelayHours: calendar.slotGenerationDelayHours,
    slotGenerationPeriodDays: calendar.slotGenerationPeriodDays,
    dailyAvailabilities: calendar.dailyAvailabilities.map(
      (da: CalendarDailyAvailability) => {
        return {
          dayOfWeek: da.dayOfWeek,
          from: {
            hours: da.from.hours,
            minutes: da.from.minutes,
          },
          until: {
            hours: da.until.hours,
            minutes: da.until.minutes,
          },
        }
      }
    ),
    assignedStaffs: assignedStaffs,
    staffSelectionStrategy: calendar.staffSelectionStrategy,
    questions: fromGQLCalendarQuestions(calendar.questions),
  }
}

export const mapFormToCalendarSettingInput = (
  form: FormType
): CalendarSettingInput => {
  const assignedStaffs = convertFormStyleAssignedStaffsToGQLInputStyle(
    form.assignedStaffs,
    form.staffSelectionStrategy
  )

  const questions = form.questions.map((q: Question, index: number) =>
    toGQLCalendarQuestionInput(q, index)
  )

  const res: CalendarSettingInput = {
    privateTitle: form.privateTitle.length ? form.privateTitle : undefined,
    publicTitle: form.publicTitle,
    status: form.status,
    description: form.description.length ? form.description : undefined,
    slotDurationMinutes: form.durationMinutes,
    slotOffsetMinutes: form.offsetMinutes,
    slotGenerationDelayHours: form.slotGenerationDelayHours,
    slotGenerationPeriodDays: form.slotGenerationPeriodDays,
    dailyAvailabilities: form.dailyAvailabilities,
    assignedStaffs: assignedStaffs,
    staffSelectionStrategy: form.staffSelectionStrategy,
    questions: questions,
  }
  return res
}
